import {take} from "rxjs/operators";
import {AppsGroups, AppsGroupsReferenceResolver, FlowDescriptionEndpoint, ResolvedAppsGroup, AppsGroupsData} from "@app/netscope/model/data-models";
import {
  AppsGroupsFiles,
  NetscopeApiService,
  Success,
  Response
} from "@app/services/netscope-api.service";
import {resourceUuidLabel} from "@app/netscope/model/ip-addresses";

/**
 * Resolves the apps groups other parts of DC Netscope point to by name and file, the flows descriptions being
 * the first of them. The files are loaded on demand and kept: a page resolving the same groups over and over
 * asks for each file once.
 *
 * An apps group stands for its members and for the members of the apps groups it includes, the inclusions being
 * followed across files as the apps groups view does it.
 */
export class AppsGroupsResolver implements AppsGroupsReferenceResolver {
  // Apps groups of every file loaded, a file that could not be loaded holding none
  private appsGroupsByFile: Map<string, AppsGroups[]> = new Map();
  private pendingFiles: Map<string, Promise<void>> = new Map();

  files: string[] = [];
  generatedFiles: string[] = [];

  constructor(private netscopeService: NetscopeApiService) {
  }

  // Lists the apps groups files, so that the user can pick a group in any of them
  loadFilesList = async () => {
    let response = await this.netscopeService.getAppsGroupsFiles().pipe(take(1)).toPromise();

    if (response instanceof Success) {
      let appsGroupsFiles: AppsGroupsFiles = response.element;
      this.files = appsGroupsFiles.files ?? [];
      this.generatedFiles = appsGroupsFiles.generatedFiles ?? [];
    }
  }

  /**
   * Loads the files holding the given apps groups, and the files their inclusions point into in turn. A file is
   * never asked for twice, even when it is still being loaded.
   */
  loadFiles = async (files: string[]) => {
    let wantedFiles = Array.from(new Set(files.filter((file) => file != undefined)));

    while (wantedFiles.length > 0) {
      await Promise.all(wantedFiles.map((file) => this.loadFile(file)));

      let includedFiles: Set<string> = new Set();
      for (let file of wantedFiles) {
        for (let appGroup of this.appsGroupsByFile.get(file) ?? []) {
          for (let included of appGroup.included_apps_groups ?? []) {
            let includedFile = included.file ?? file;
            if (!this.appsGroupsByFile.has(includedFile)) {
              includedFiles.add(includedFile);
            }
          }
        }
      }

      wantedFiles = Array.from(includedFiles);
    }
  }

  private loadFile = (file: string): Promise<void> => {
    if (this.appsGroupsByFile.has(file)) {
      return Promise.resolve();
    }

    if (!this.pendingFiles.has(file)) {
      let pending = this.netscopeService.getAppsGroups(file).pipe(take(1)).toPromise()
        .then((response: Response<AppsGroupsData>) => {
          let appsGroups = response instanceof Success
            ? response.element?.apps_groups ?? []
            : [];
          this.appsGroupsByFile.set(file, appsGroups);
        })
        .catch(() => {
          this.appsGroupsByFile.set(file, []);
        })
        .finally(() => {
          this.pendingFiles.delete(file);
        });
      this.pendingFiles.set(file, pending);
    }

    return this.pendingFiles.get(file);
  }

  // Apps groups of a loaded file, or undefined while the file was not loaded
  appsGroupsOf = (file: string): AppsGroups[] => {
    return this.appsGroupsByFile.get(file);
  }

  resolve = (name: string, file: string): ResolvedAppsGroup => {
    let result = new ResolvedAppsGroup();
    let appGroup = this.appGroupIn(file, name);

    if (appGroup == undefined) {
      return result;
    }

    let membersByUuid: Map<string, {uuid: string, name?: string}> = new Map();
    for (let member of this.membersOf(appGroup, file, new Set())) {
      if (!membersByUuid.has(member.uuid)) {
        membersByUuid.set(member.uuid, member);
      }
    }

    result.found = true;
    result.members = Array.from(membersByUuid.values());
    return result;
  }

  private appGroupIn = (file: string, name: string): AppsGroups => {
    return (this.appsGroupsByFile.get(file) ?? []).find((appGroup) => appGroup.name == name);
  }

  // A group already walked is not walked twice, so that groups including each other cannot loop
  private membersOf = (appGroup: AppsGroups, file: string, visited: Set<string>): {uuid: string, name?: string}[] => {
    if (appGroup == undefined || visited.has(`${file}/${appGroup.name}`)) {
      return [];
    }
    visited.add(`${file}/${appGroup.name}`);

    let members = (appGroup.vms ?? []).concat(appGroup.matched_vms ?? []);
    for (let included of appGroup.included_apps_groups ?? []) {
      // An inclusion naming no file points inside the file holding the group that carries it
      let includedFile = included.file ?? file;
      members = members.concat(this.membersOf(this.appGroupIn(includedFile, included.name), includedFile, visited));
    }

    return members;
  }
}

/**
 * What an end of a network rule stands for, to show it to the user. An apps group is shown with the resources
 * it stands for right now, as a rule pointing to it follows it: a resource added to the group is covered by the
 * rule, authorized when the description is, without the rule being edited.
 */
export class FlowDescriptionEndpointDescription {
  label: string;
  // Details shown on hover: the resources an apps group stands for
  title: string;
  // An apps group that was not found: the end matches no flow
  missing: boolean = false;
  // Number of resources an apps group stands for right now, once its file is loaded
  membersCount: number;
}

export function describeEndpoint(endpoint: FlowDescriptionEndpoint, resolver: AppsGroupsResolver,
                                 resourceName: (uuid: string) => string = () => undefined): FlowDescriptionEndpointDescription {
  let description = new FlowDescriptionEndpointDescription();
  description.label = endpoint.label();
  description.title = endpoint.label();

  if (endpoint.kind != "apps_group") {
    return description;
  }

  // The file is not loaded yet: nothing can be told about the group so far
  if (resolver?.appsGroupsOf(endpoint.file) == undefined) {
    description.title = `Apps group '${endpoint.name}' of the file '${endpoint.file}'`;
    return description;
  }

  let resolved = resolver.resolve(endpoint.name, endpoint.file);

  if (!resolved.found) {
    description.missing = true;
    description.label = `${endpoint.label()} · not found`;
    description.title = `The apps group '${endpoint.name}' was not found in the file '${endpoint.file}': `
      + `this end matches no flow`;
    return description;
  }

  // At most a screenful of names, the count telling the rest
  let shownCount = 20;
  let names = resolved.members
    .slice(0, shownCount)
    .map((member) => resourceName(member.uuid) ?? member.name ?? resourceUuidLabel(member.uuid));
  let remaining = resolved.members.length - names.length;

  description.membersCount = resolved.members.length;
  description.label = `${endpoint.label()} · ${resolved.members.length}`;
  description.title = `Apps group '${endpoint.name}' of the file '${endpoint.file}', `
    + `standing right now for ${resolved.members.length} member(s):\n`
    + names.join("\n")
    + (remaining > 0 ? `\n… and ${remaining} more` : "");

  return description;
}
